import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { techniciansApi, ApiError } from "../lib/api";
import { useConfirmGate } from "../lib/useConfirm";
import { Spinner } from "../components/ui-modern";
import {
  ModernBreadcrumbs,
  ModernButton,
  ModernCard,
  ModernConfirmDialog,
  ModernErrorBanner,
  ModernField,
  ModernInput,
} from "../components/ui-modern";

/**
 * The modern theme's Technician Edit screen — see docs/FRONTEND_BACKLOG.md
 * "Round 62" (the round covering pages outside the original 11 mockup
 * screens). No dedicated mockup screen exists for this page; it reuses the
 * established modern vocabulary (ModernCard/ModernField/ModernInput/
 * ModernBreadcrumbs/ModernButton) from screens that already exist, same as
 * TechniciansListPageModern.tsx's own edit/create form styling.
 *
 * Same data-fetching/mutations as TechnicianEditPageClassic.tsx — identical
 * query key (`["technician", id]`), identical `updateMutation`/
 * `setActiveMutation` `mutationFn`/`onSuccess`/`onError`, duplicated here as
 * a thin re-run of Classic's own calls rather than a shared hook, matching
 * every other screen's own tradeoff.
 */
export function TechnicianEditPageModern() {
  const { technicianId } = useParams();
  const id = Number(technicianId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [formError, setFormError] = useState<string | null>(null);
  const confirmGate = useConfirmGate();
  const statusConfirm = useConfirmGate();

  const techQuery = useQuery({ queryKey: ["technician", id], queryFn: () => techniciansApi.get(id) });
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [code, setCode] = useState<string | null>(null);

  const updateMutation = useMutation({
    mutationFn: () => techniciansApi.update(id, { displayName: displayName ?? undefined, code: code ?? undefined }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["technicians"] });
      navigate("/technicians");
    },
    onError: (err) => setFormError(err instanceof ApiError ? err.message : "Could not save this technician."),
  });

  const setActiveMutation = useMutation({
    mutationFn: (isActive: boolean) => (isActive ? techniciansApi.activate(id) : techniciansApi.deactivate(id)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["technician", id] });
      void queryClient.invalidateQueries({ queryKey: ["technicians"] });
    },
    onError: (err) => setFormError(err instanceof ApiError ? err.message : "Could not update this technician's status."),
  });

  if (techQuery.isLoading) return <Spinner label="Loading technician…" />;
  if (techQuery.error || !techQuery.data) return <ModernErrorBanner message="Could not find that technician." />;

  const technician = techQuery.data.technician;
  const nameValue = displayName ?? technician.displayName;
  const codeValue = code ?? technician.code ?? "";

  return (
    <div className="mx-auto max-w-xl">
      <ModernBreadcrumbs items={[{ label: "Technicians", to: "/technicians" }, { label: `Edit ${technician.displayName}` }]} />
      <h1 className="m-0 mb-4 text-[27px] font-bold text-[#071a33]">Edit Technician</h1>
      {formError && <ModernErrorBanner message={formError} />}

      <ModernCard>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            confirmGate.request(() => updateMutation.mutate());
          }}
        >
          <ModernField label="Name" htmlFor="displayNameModern" required>
            <ModernInput id="displayNameModern" required uppercase value={nameValue} onChange={(e) => setDisplayName(e.target.value)} />
          </ModernField>
          <ModernField label="Code" htmlFor="codeModern">
            <ModernInput id="codeModern" uppercase value={codeValue} onChange={(e) => setCode(e.target.value)} />
            <p className="mt-1.5 text-[12.5px] text-[#8b97a8]">Optional short code or badge number</p>
          </ModernField>
          <div className="flex flex-wrap gap-2.5">
            <ModernButton type="submit" disabled={updateMutation.isPending}>
              Save Changes
            </ModernButton>
            <ModernButton
              type="button"
              variant="secondary"
              onClick={() => statusConfirm.request(() => setActiveMutation.mutate(!technician.isActive))}
              disabled={setActiveMutation.isPending}
            >
              {technician.isActive ? "Deactivate" : "Reactivate"}
            </ModernButton>
            <ModernButton type="button" variant="ghost" onClick={() => navigate("/technicians")}>
              Cancel
            </ModernButton>
          </div>
        </form>
      </ModernCard>

      <ModernConfirmDialog {...confirmGate.dialogProps} message="Save these changes to the technician?" />
      <ModernConfirmDialog
        {...statusConfirm.dialogProps}
        message={technician.isActive ? "Deactivate this technician? They'll be hidden from new assignments." : "Reactivate this technician?"}
      />
    </div>
  );
}
